import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  ChevronLeft, 
  ChevronRight, 
  Wind, 
  Moon, 
  Shield, 
  Heart, 
  Anchor, 
  Target, 
  MessageCircle, 
  Eye, 
  HeartHandshake, 
  Sun,
  Sparkles
} from "lucide-react";
import { LithaeLogo, LithaeIcon } from "@/components/LithaeLogo";
import { STONES, AVAILABLE_NEEDS, PROTECTION_TYPES, SENSITIVITY_LEVELS, INTENSITY_PREFERENCES, USAGE_CONTEXTS, CHAKRA_OPTIONS } from "@/data/stones";
import { getTopCandidates, recommend } from "@/utils/recommend";

const STORAGE_KEY = "quiz_profile";
const ANSWERS_KEY = "quiz_answers";

// Icon mapping
const iconMap = {
  wind: Wind,
  moon: Moon,
  shield: Shield,
  heart: Heart,
  anchor: Anchor,
  target: Target,
  'message-circle': MessageCircle,
  eye: Eye,
  'heart-handshake': HeartHandshake,
  sun: Sun
};

const Quiz = () => {
  const navigate = useNavigate();
  
  // Initialize state from localStorage
  const [profile, setProfile] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {
      goals: [],
      protectionMode: null,
      sensitivity: null,
      intensityPref: null,
      usage: null,
      chakra: null
    };
  });

  const [answers, setAnswers] = useState(() => {
    const saved = localStorage.getItem(ANSWERS_KEY);
    return saved ? JSON.parse(saved) : {};
  });

  const [currentStep, setCurrentStep] = useState(0);
  
  // Calculate candidates using useMemo
  const candidates = useMemo(() => {
    return getTopCandidates(STONES, profile);
  }, [profile]);

  // Define questions based on profile (conditional logic)
  const getQuestions = useCallback(() => {
    const questions = [
      {
        id: 'goals',
        title: 'Quel est votre besoin principal ?',
        subtitle: 'Sélectionnez un ou plusieurs besoins',
        type: 'multi',
        options: AVAILABLE_NEEDS.map(need => ({
          id: need.id,
          label: need.label,
          icon: need.icon
        })),
        minSelect: 1,
        maxSelect: 3
      }
    ];

    // Conditional: protection mode if protection is selected
    if (profile.goals.includes('protection')) {
      questions.push({
        id: 'protectionMode',
        title: 'Quel type de protection recherchez-vous ?',
        subtitle: 'Chaque type offre une approche différente',
        type: 'single',
        options: PROTECTION_TYPES.map(type => ({
          id: type.id,
          label: type.label,
          description: type.description
        }))
      });
    }

    // Sensitivity question
    questions.push({
      id: 'sensitivity',
      title: 'Comment décririez-vous votre sensibilité énergétique ?',
      subtitle: 'Cela nous aide à ajuster les recommandations',
      type: 'single',
      options: SENSITIVITY_LEVELS.map(level => ({
        id: level.id,
        label: level.label,
        description: level.description
      }))
    });

    // Intensity preference
    questions.push({
      id: 'intensityPref',
      title: 'Quelle intensité d\'énergie préférez-vous ?',
      subtitle: 'Certaines pierres ont une énergie plus marquée',
      type: 'single',
      options: INTENSITY_PREFERENCES.map(pref => ({
        id: pref.id,
        label: pref.label,
        description: pref.description
      }))
    });

    // Usage context
    questions.push({
      id: 'usage',
      title: 'Comment souhaitez-vous utiliser votre pierre ?',
      subtitle: 'Le contexte d\'utilisation influence le choix',
      type: 'single',
      options: USAGE_CONTEXTS.map(ctx => ({
        id: ctx.id,
        label: ctx.label,
        description: ctx.description
      }))
    });

    // Optional chakra question
    questions.push({
      id: 'chakra',
      title: 'Avez-vous un chakra à travailler en particulier ?',
      subtitle: 'Question optionnelle',
      type: 'single',
      optional: true,
      options: CHAKRA_OPTIONS.map(chakra => ({
        id: chakra.id,
        label: chakra.label,
        description: chakra.description,
        color: chakra.color
      }))
    });

    return questions;
  }, [profile.goals]);

  const questions = getQuestions();
  const totalSteps = questions.length;
  const currentQuestion = questions[currentStep];
  const progressPercent = ((currentStep + 1) / totalSteps) * 100;

  // Save to localStorage on profile change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    localStorage.setItem(ANSWERS_KEY, JSON.stringify(answers));
  }, [profile, answers]);

  // Handle answer selection
  const handleSelect = (optionId) => {
    if (currentQuestion.type === 'multi') {
      const currentSelection = profile[currentQuestion.id] || [];
      let newSelection;
      
      if (currentSelection.includes(optionId)) {
        newSelection = currentSelection.filter(id => id !== optionId);
      } else {
        if (currentSelection.length < (currentQuestion.maxSelect || 999)) {
          newSelection = [...currentSelection, optionId];
        } else {
          return; // Max reached
        }
      }
      
      setProfile(prev => ({ ...prev, [currentQuestion.id]: newSelection }));
      setAnswers(prev => ({ ...prev, [currentQuestion.id]: newSelection }));
    } else {
      setProfile(prev => ({ ...prev, [currentQuestion.id]: optionId }));
      setAnswers(prev => ({ ...prev, [currentQuestion.id]: optionId }));
    }
  };

  // Check if current question is answered
  const isCurrentAnswered = () => {
    const answer = profile[currentQuestion.id];
    if (currentQuestion.optional) return true;
    if (currentQuestion.type === 'multi') {
      return answer && answer.length >= (currentQuestion.minSelect || 1);
    }
    return answer !== null && answer !== undefined;
  };

  // Check if option is selected
  const isSelected = (optionId) => {
    const answer = profile[currentQuestion.id];
    if (currentQuestion.type === 'multi') {
      return answer && answer.includes(optionId);
    }
    return answer === optionId;
  };

  // Navigation
  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleFinish = () => {
    // Calculate final recommendations
    const results = recommend(STONES, profile);
    
    // Navigate to results with state
    navigate('/resultats', { 
      state: { 
        results, 
        profile 
      } 
    });
  };

  const isLastStep = currentStep === totalSteps - 1;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm">Retour</span>
            </button>
            <div className="flex items-center gap-2">
              <LithaeIcon size={20} />
              <span className="font-serif text-lg font-medium text-foreground">
                Quiz Lithae
              </span>
            </div>
            <div className="w-16" /> {/* Spacer */}
          </div>
        </div>
      </header>

      {/* Progress bar */}
      <div className="fixed top-16 left-0 right-0 z-40 bg-background">
        <Progress value={progressPercent} className="h-1 rounded-none" />
        <div className="max-w-3xl mx-auto px-4 py-2 flex justify-between items-center">
          <span className="text-xs text-muted-foreground">
            Question {currentStep + 1} sur {totalSteps}
          </span>
          {candidates.length > 0 && (
            <span className="text-xs text-primary flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              {candidates.filter(c => c.score > 0).length} pierres correspondent
            </span>
          )}
        </div>
      </div>

      {/* Main content */}
      <main className="pt-32 pb-24 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Question */}
          <div className="text-center mb-8 animate-fade-in" key={currentStep}>
            <h1 className="font-serif text-2xl sm:text-3xl font-semibold text-foreground mb-3">
              {currentQuestion.title}
            </h1>
            <p className="text-muted-foreground">
              {currentQuestion.subtitle}
            </p>
            {currentQuestion.type === 'multi' && (
              <p className="text-xs text-primary mt-2">
                {profile[currentQuestion.id]?.length || 0} / {currentQuestion.maxSelect} sélectionné(s)
              </p>
            )}
          </div>

          {/* Options */}
          <div className="space-y-3 animate-slide-up">
            {currentQuestion.options.map((option, index) => {
              const Icon = option.icon ? iconMap[option.icon] : null;
              const selected = isSelected(option.id);
              
              return (
                <button
                  key={option.id}
                  onClick={() => handleSelect(option.id)}
                  className={`
                    w-full p-4 sm:p-5 rounded-xl border-2 text-left
                    transition-all duration-300 
                    ${selected 
                      ? 'border-primary bg-primary/5 shadow-soft' 
                      : 'border-border bg-card hover:border-primary/30 hover:bg-muted/30'
                    }
                  `}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="flex items-start gap-4">
                    {Icon && (
                      <div className={`
                        w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
                        ${selected ? 'bg-primary/20' : 'bg-muted'}
                      `}>
                        <Icon className={`w-5 h-5 ${selected ? 'text-primary' : 'text-muted-foreground'}`} />
                      </div>
                    )}
                    {option.color && (
                      <div className={`
                        w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
                        ${selected ? 'bg-primary/20' : 'bg-muted'}
                      `}>
                        <div className={`w-5 h-5 rounded-full bg-${option.color}`} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium ${selected ? 'text-foreground' : 'text-foreground/90'}`}>
                        {option.label}
                      </p>
                      {option.description && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {option.description}
                        </p>
                      )}
                    </div>
                    <div className={`
                      w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0
                      transition-all duration-300
                      ${selected 
                        ? 'border-primary bg-primary' 
                        : 'border-border'
                      }
                    `}>
                      {selected && (
                        <svg className="w-3 h-3 text-primary-foreground" fill="currentColor" viewBox="0 0 12 12">
                          <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
                        </svg>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </main>

      {/* Navigation footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-background/80 backdrop-blur-md border-t border-border z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            <Button
              variant="ghost"
              onClick={handlePrev}
              disabled={currentStep === 0}
              className="flex items-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Retour
            </Button>
            
            {isLastStep ? (
              <Button
                variant="primary"
                onClick={handleFinish}
                disabled={!isCurrentAnswered() && !currentQuestion.optional}
                className="flex items-center gap-2"
              >
                Voir mes résultats
                <Sparkles className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleNext}
                disabled={!isCurrentAnswered()}
                className="flex items-center gap-2"
              >
                Continuer
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Quiz;
